<script lang="ts">
  import { fade, slide } from "svelte/transition";
  import { createEventDispatcher, onDestroy, onMount } from "svelte";
  import { portal } from "svelte-portal/src/Portal.svelte";
  import { clickOutside } from "$lib/utils/clickOutside";
  import { modals } from "$lib/stores/modal";

  export let delay = 6000;
  export let isOpen = false;

  export let disableAutoDismiss = false;

  export let fixed = false;

  let viewportWidth: number;

  let containerMax = 2560;

  let right = 0;

  $: viewportWidth >= containerMax
    ? (right = (viewportWidth - containerMax) / 2)
    : 0;

  onMount(() => {
    const bodyStyle = getComputedStyle(document.body);
    containerMax = Number(
      bodyStyle.getPropertyValue("--container-max").replace(/\D/g, ""),
    );
  });

  let timeout;
  let hovered = false;

  let isSafari =
    typeof navigator !== "undefined"
      ? /^(?!.*chrome).*safari/i.test(navigator.userAgent)
      : false;

  $: transitionToUse = isSafari ? fade : slide;

  const dispatch = createEventDispatcher<{
    close: any;
    open: any;
    startTimeout: any;
    cancelTimeout: any;
  }>();

  export const startTimeout = (event: any = {}) => {
    if (!disableAutoDismiss && !hovered && !$modals.sizeGuide) {
      timeout = setTimeout(function () {
        close(event);
      }, delay);
    }
  };

  export const cancelTimeout = () => {
    if (!disableAutoDismiss || hovered || $modals.sizeGuide) {
      clearTimeout(timeout);
    }
  };

  export const open = (event: any = {}) => {
    isOpen = true;
    dispatch("open", { isOpen, ...event });
    dispatch("startTimeout", { event });
    const nav = document.getElementsByClassName("nav")[0] as HTMLElement;
    if (nav) nav.classList.add("nav--popover-open");
  };

  export const close = (event: any = {}) => {
    isOpen = false;
    dispatch("close", { isOpen, ...event });
    dispatch("cancelTimeout");
    const nav = document.getElementsByClassName("nav")[0] as HTMLElement;
    if (nav) nav.classList.remove("nav--popover-open");
  };

  let navBottom = 0;

  const isBrowser = typeof window !== "undefined";

  $: if (isBrowser && isOpen) {
    navBottom =
      document.getElementsByTagName("nav")[0]?.getBoundingClientRect().bottom ||
      0;
  }

  let interval: any;

  onMount(() => {
    document.addEventListener("scroll", event => {
      if (isOpen && !hovered && !$modals.sizeGuide) {
        close(event);
      }
    });
    startTimeout();
  });

  $: if (isOpen || hovered) {
    if (!interval) {
      interval = setInterval(() => {
        handleScroll();
      }, 100);
    }
  } else {
    if (interval) {
      clearInterval(interval);
      interval = null;
    }
  }

  function handleScroll() {
    if (isBrowser)
      navBottom =
        document.getElementsByTagName("nav")[0]?.getBoundingClientRect()
          .bottom || 0;
  }

  onDestroy(() => clearTimeout(timeout));
</script>

<style lang="scss">
  .popover {
    display: flex;
    z-index: 1003;
    position: absolute;
    top: var(--popover-offset);
    right: var(--right-offset);
    width: 28rem;

    &--fixed {
      position: fixed;
    }
  }

  .dialog {
    width: 100%;
    background-color: $background;
  }
  .cover {
    background-color: $on-surface-10;
    position: fixed;
    z-index: 10;
    height: 100vh;
    width: 100vw;
    top: 0px;
  }
</style>

<svelte:window on:scroll={handleScroll} bind:innerWidth={viewportWidth} />

{#if isOpen}
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
  <div
    style="--popover-offset: {navBottom}px; --right-offset: {right}px"
    use:portal
    class={`popover ${fixed && "popover--fixed"}`}
    on:keydown
    on:click|self={close}
    use:clickOutside
    on:click-outside={() => {
      if ($modals.sizeGuide) return;
      close();
    }}
    on:scroll|self={close}
  >
    <div
      transition:transitionToUse={{ duration: 750 }}
      on:introend={startTimeout}
      on:mouseenter={() => {
        hovered = true;
        cancelTimeout();
      }}
      on:mouseleave={() => {
        hovered = false;
        startTimeout();
      }}
      class="dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal"
      tabindex="0"
    >
      <slot />
    </div>
  </div>
  <div use:portal transition:fade={{ duration: 750 }} class="cover" />
{/if}
